import { useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { UserContext } from '../../context';
import { NewsFeedScope, StoryCardStatus } from '../../services';
import { getNewsFeed, getPinnedFeed, loadMoreNewsFeed, loadMorePinnedFeed } from '../../slices';
import { NEWS_FEED_ADMIN_COOKIE_KEY } from '../constants';
import { isAdminLayout } from '../utils';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useLastDate } from './useLastDate';

export const useNewsFeed = () => {
  const dispatch = useAppDispatch();

  const { getDateRange } = useLastDate();

  const [cookies] = useCookies([NEWS_FEED_ADMIN_COOKIE_KEY]);

  const { page } = useAppSelector(({ newsFeed }) => newsFeed);

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { requestFilter = { scope: NewsFeedScope.WEB_FEED } } = {
    ...cookies[NEWS_FEED_ADMIN_COOKIE_KEY],
  };

  const { dateRange, pinnedStorySection, ...requestFilterRest } = requestFilter;

  const filter = useMemo(() => {
    if (!isAdminLayout()) {
      return { status: [StoryCardStatus.PUBLISHED] };
    }

    return { ...requestFilterRest, ...getDateRange(dateRange) };
  }, [dateRange, getDateRange, requestFilterRest]);

  const getNewsFeedItems = useCallback(
    () => dispatch(getNewsFeed({ channelId, filter })),
    [channelId, dispatch, filter]
  );

  const loadMoreNewsFeedItems = useCallback(
    () => dispatch(loadMoreNewsFeed({ channelId, page: page + 1, filter })),
    [channelId, dispatch, filter, page]
  );

  const getPinnedFeedItems = useCallback(
    (pinnedStoryId: number) => dispatch(getPinnedFeed({ storyId: pinnedStoryId, filter })),
    [dispatch, filter]
  );

  const loadMorePinnedFeedItems = useCallback(
    (pinnedStoryId: number) =>
      dispatch(loadMorePinnedFeed({ storyId: pinnedStoryId, page: page + 1, filter })),
    [dispatch, filter, page]
  );

  return {
    getNewsFeedItems,
    loadMoreNewsFeedItems,
    getPinnedFeedItems,
    loadMorePinnedFeedItems,
    pinnedStorySection,
  };
};
